import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { forgetRegion, readLastRegion, rememberRegion } from '@/lib/lastRegion';
import { useAuth } from '../../hooks/useAuth';
import { useRegions } from '../../hooks/useRegions';
import {
  useApplyUnifiImport,
  useCreateSegment,
  useDeleteSegment,
  useSegments,
  useUnifiImports,
  type ConsoleImport,
  type MemberInput,
  type MemberKind,
  type Segment,
  type ZoneImport,
  type ZoneImportAction,
} from '../../hooks/useSegments';

/**
 * Segments — the axes the segmentation matrix groups by
 * (`FirewallRulesAndPathCheck.md` §8.2).
 *
 * A segment belongs to a region, because a region is the unit within which
 * traffic can actually flow, and because two MSP customers may each run
 * 192.168.1.0/24 and each call it "Office". So the page shows one region at a
 * time, chosen as the matrix chooses it and named in the URL: an MSP with a
 * region per customer reads one customer's segments, not every customer's.
 *
 * The page keeps two things visibly apart: what somebody DECLARED (member
 * rules) and what that currently RESOLVES to (addresses). Membership is
 * authored once and re-resolved against live inventory, so the second changes
 * without anyone touching the first, and a reader needs to see which is which.
 */

const KINDS: { value: MemberKind; label: string; placeholder: string; help: string }[] = [
  { value: 'cidr', label: 'Network', placeholder: '10.70.3.0/24', help: 'A subnet, written as CIDR.' },
  { value: 'vlan', label: 'VLAN', placeholder: '20', help: 'Every endpoint currently seen on this VLAN.' },
  { value: 'interface', label: 'Interface', placeholder: 'fw-01:port3', help: 'Everything behind one device interface.' },
  { value: 'endpoint', label: 'Endpoint', placeholder: 'endpoint id', help: 'One specific host.' },
];

function MemberRow({ m, onChange, onRemove }: { m: MemberInput; onChange: (m: MemberInput) => void; onRemove: () => void }) {
  const kind = KINDS.find((k) => k.value === m.kind) ?? KINDS[0];
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Select value={m.kind} onValueChange={(v) => {
          onChange({ ...m, kind: v as MemberKind });
        }}>
        <SelectTrigger className="w-36">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {KINDS.map((k) => (
            <SelectItem key={k.value} value={k.value}>
              {k.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Input
        className="w-56"
        value={m.value}
        placeholder={kind.placeholder}
        onChange={(e) => {
          onChange({ ...m, value: e.target.value });
        }}
      />
      {/* Exclusion is why a hole in a segment needs no second segment. */}
      <Button
        type="button"
        variant={m.negated ? 'default' : 'outline'}
        size="sm"
        onClick={() => {
          onChange({ ...m, negated: !m.negated });
        }}
        title="Exclude these addresses instead of including them"
      >
        {m.negated ? 'Excluded' : 'Included'}
      </Button>
      <span className="text-xs text-fg-subtle flex-1 min-w-0">{kind.help}</span>
      <Button type="button" variant="ghost" size="sm" onClick={onRemove}>
        Remove
      </Button>
    </div>
  );
}

function SegmentCard({ segment, onDelete }: { segment: Segment; onDelete: (id: string) => void }) {
  return (
    <div className="rounded-lg border border-border p-4 flex flex-col gap-3">
      <div className="flex items-baseline gap-3 flex-wrap">
        <h3 className="font-semibold">{segment.name}</h3>
        <span className="text-xs text-fg-subtle">
          {segment.addressCount.toLocaleString()} address{segment.addressCount === 1 ? '' : 'es'}
        </span>
        <Button variant="ghost" size="sm" className="ml-auto" onClick={() => {
            onDelete(segment.id);
          }}>
          Delete
        </Button>
      </div>
      {segment.description && <p className="text-sm text-fg-subtle">{segment.description}</p>}

      <div className="flex flex-wrap gap-1">
        {segment.members.map((m) => (
          <Badge key={m.id} variant={m.negated ? 'destructive' : 'secondary'} title={m.detail ?? undefined}>
            {m.negated ? 'except ' : ''}
            {m.kind} {m.value}
          </Badge>
        ))}
      </div>

      {/* What it declares, versus what that resolves to right now. */}
      {segment.resolved.length > 0 && (
        <div className="text-xs font-mono text-fg-subtle break-all">{segment.resolved.join('  ')}</div>
      )}

      {/* D25: a rule naming something we cannot find is reported, never dropped. */}
      {segment.unresolved.length > 0 && (
        <div className="rounded border border-sev-3 bg-sev-3-soft p-2 text-xs text-sev-3 flex flex-col gap-1">
          {segment.unresolved.map((u) => (
            <div key={u.ruleId}>
              <span className="font-medium">
                {u.kind} {u.value}
              </span>{' '}
              — {u.reason}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const ACTION_LABEL: Record<ZoneImportAction, string> = {
  create: 'new',
  update: 'update',
  unchanged: 'unchanged',
  remove: 'remove',
  blocked: "can't import",
};

function ZoneRow({ z }: { z: ZoneImport }) {
  const changes = [...z.added.map((a) => `+ ${a}`), ...z.removed.map((r) => `− ${r}`)];
  return (
    <li className="flex flex-col gap-1 py-2 border-t border-border first:border-t-0">
      <div className="flex items-baseline gap-2 flex-wrap">
        <span className="font-medium">{z.segmentName}</span>
        {z.zone !== null && z.zone !== z.segmentName && <span className="text-xs text-fg-subtle">zone {z.zone}</span>}
        <Badge variant={z.action === 'remove' || z.action === 'blocked' ? 'destructive' : 'secondary'}>
          {ACTION_LABEL[z.action]}
        </Badge>
      </div>
      {z.action === 'blocked' && z.reason && <p className="text-xs text-sev-3">{z.reason}</p>}
      {z.action !== 'remove' && (
        <p className="text-xs font-mono text-fg-subtle break-words">
          {z.members.length > 0 ? z.members.join('  ') : 'no members'}
        </p>
      )}
      {z.action === 'update' && changes.length > 0 && (
        <p className="text-xs font-mono text-fg-subtle break-words">{changes.join('  ')}</p>
      )}
      {z.leftOut.length > 0 && <p className="text-xs text-fg-subtle">Left out: {z.leftOut.join('; ')}</p>}
    </li>
  );
}

/**
 * A UniFi console's firewall zones, offered as segments. One-time and
 * re-runnable: importing again updates only the segments an import made, and
 * the list shows exactly what that will change before anything is written.
 */
function UnifiImport({ consoleImport: c }: { consoleImport: ConsoleImport }) {
  const apply = useApplyUnifiImport();
  const [error, setError] = useState<string | null>(null);
  const pending = c.zones.filter((z) => z.action === 'create' || z.action === 'update' || z.action === 'remove').length;

  const run = () => {
    setError(null);
    apply.mutate(
      { deviceId: c.deviceId, snapshotId: c.snapshotId },
      {
        onError: (e: unknown) => {
          const body = (e as { response?: { data?: { error?: string; description?: string } } }).response?.data;
          setError(body?.description ?? body?.error ?? 'Could not import the zones.');
        },
      },
    );
  };

  return (
    <div className="rounded-lg border border-border p-4 flex flex-col gap-2">
      <div className="flex items-baseline gap-3 flex-wrap">
        <h2 className="font-semibold">Import zones from {c.deviceName}</h2>
        <span className="text-xs text-fg-subtle">
          {c.regionName} · UniFi snapshot {c.snapshotId.slice(0, 8)} · {new Date(c.capturedAt).toLocaleString()}
        </span>
      </div>
      <p className="text-sm text-fg-subtle max-w-prose">
        One segment per UniFi zone, with each enabled network&apos;s subnet as a member. Importing again updates the
        segments an import made, including any members added to them by hand, and leaves your own segments alone.
      </p>
      <ul className="flex flex-col">
        {c.zones.map((z) => (
          <ZoneRow key={z.zoneId} z={z} />
        ))}
      </ul>
      <div className="flex gap-2 items-center">
        <Button type="button" disabled={pending === 0 || apply.isPending} onClick={run}>
          {apply.isPending ? 'Importing…' : pending === 0 ? 'Up to date' : `Import ${String(pending)} zone${pending === 1 ? '' : 's'}`}
        </Button>
        {error && <p className="text-sm text-sev-3">{error}</p>}
      </div>
    </div>
  );
}

export default function Segments() {
  const { user } = useAuth();
  const { regionId } = useParams<{ regionId?: string }>();
  const navigate = useNavigate();
  const { data: segments, isLoading } = useSegments();
  const { data: regions } = useRegions(user?.organizationId);
  const { data: imports } = useUnifiImports();
  const createSegment = useCreateSegment();
  const deleteSegment = useDeleteSegment();

  const [name, setName] = useState('');
  const [members, setMembers] = useState<MemberInput[]>([{ kind: 'cidr', value: '', negated: false }]);
  const [error, setError] = useState<string | null>(null);

  // The region in the URL, once the regions this account can see confirm it.
  const region = regions?.find((r) => r.id === regionId);

  // The bare path resolves a region and rewrites the URL to it; a URL naming
  // one that does not resolve (deleted, or another organization's) drops back
  // to the bare path. Both replace, so Back never lands on a redirect.
  useEffect(() => {
    if (!regions) return;
    if (regionId !== undefined) {
      if (!regions.some((r) => r.id === regionId)) {
        forgetRegion(regionId);
        void navigate('/segments', { replace: true });
      }
      return;
    }
    const last = readLastRegion();
    const target = regions.find((r) => r.id === last)?.id ?? regions[0]?.id;
    if (target) void navigate(`/segments/${target}`, { replace: true });
  }, [regionId, regions, navigate]);

  useEffect(() => {
    if (region) rememberRegion(region.id);
  }, [region]);

  const shown = (segments ?? []).filter((s) => s.regionId === region?.id);
  const consoles = (imports ?? []).filter((c) => c.regionId === region?.id);
  const canSubmit = region !== undefined && name.trim() !== '' && members.some((m) => m.value.trim() !== '');

  const submit = () => {
    if (!region) return;
    setError(null);
    createSegment.mutate(
      {
        regionId: region.id,
        name: name.trim(),
        members: members.filter((m) => m.value.trim() !== ''),
      },
      {
        onSuccess: () => {
          setName('');
          setMembers([{ kind: 'cidr', value: '', negated: false }]);
        },
        onError: (e: unknown) => {
          // The backend rejects unknown kinds and empty values with a reason;
          // showing it beats a generic failure the user cannot act on.
          const body = (e as { response?: { data?: { error?: string; description?: string } } }).response?.data;
          setError(body?.description ?? body?.error ?? 'Could not create the segment.');
        },
      },
    );
  };

  return (
    <div className="p-6 max-w-[1000px]">
      <div className="flex items-baseline gap-3 flex-wrap">
        <h1 className="text-2xl md:text-3xl font-semibold tracking-tight">Segments</h1>
        <span className="text-sm text-fg-subtle">What the segmentation matrix groups by</span>
        {(regions ?? []).length > 0 && (
          <select
            className="ml-auto rounded border border-border bg-transparent px-2 py-1 text-sm"
            aria-label="Region"
            value={region?.id ?? ''}
            onChange={(e) => {
              // A push, not a replace: switching region is a navigation the
              // user should be able to undo with Back.
              void navigate(`/segments/${e.target.value}`);
            }}
          >
            {(regions ?? []).map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>
        )}
      </div>

      <p className="mt-2 text-sm text-fg-subtle max-w-prose">
        A segment belongs to a region, because a region is the space traffic can actually cross. Two customers can each
        have an &ldquo;Office&rdquo; without colliding.
      </p>

      {regions?.length === 0 && (
        <p className="mt-6 text-sm text-fg-subtle">
          This organization has no regions yet, so there is nowhere to put a segment.
        </p>
      )}

      {consoles.map((c) => (
        <div key={c.deviceId} className="mt-6">
          <UnifiImport consoleImport={c} />
        </div>
      ))}

      {region && (
        <div className="mt-6 rounded-lg border border-border p-4 flex flex-col gap-3">
          <h2 className="font-semibold">New segment in {region.name}</h2>
          <div className="flex flex-wrap gap-3 items-end">
            <div className="flex flex-col gap-1">
              <Label htmlFor="seg-name">Name</Label>
              <Input id="seg-name" className="w-56" value={name} onChange={(e) => {
                  setName(e.target.value);
                }} placeholder="DMZ" />
            </div>
          </div>

          <Label>Members</Label>
          <div className="flex flex-col gap-2">
            {members.map((m, i) => (
              <MemberRow
                key={i}
                m={m}
                onChange={(next) => {
                  setMembers(members.map((x, j) => (i === j ? next : x)));
                }}
                onRemove={() => {
                  setMembers(members.filter((_, j) => j !== i));
                }}
              />
            ))}
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="outline" size="sm" onClick={() => {
                setMembers([...members, { kind: 'cidr', value: '', negated: false }]);
              }}>
              Add member
            </Button>
            <Button type="button" disabled={!canSubmit || createSegment.isPending} onClick={submit}>
              {createSegment.isPending ? 'Creating…' : 'Create segment'}
            </Button>
          </div>
          {error && <p className="text-sm text-sev-3">{error}</p>}
        </div>
      )}

      <div className="mt-6 flex flex-col gap-3">
        {isLoading && <Skeleton className="h-32 w-full" />}
        {!isLoading && region && shown.length === 0 && (
          <p className="text-sm text-fg-subtle">
            No segments in {region.name} yet. Until one exists the matrix falls back to the region&apos;s VLANs.
          </p>
        )}
        {shown.map((s) => (
          <SegmentCard key={s.id} segment={s} onDelete={(id) => {
              deleteSegment.mutate(id);
            }} />
        ))}
      </div>
    </div>
  );
}
